import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Banner, Button, Card, Field, Input, Select } from "@magarac/ui";
import { newId } from "@magarac/shared/ids";
import {
  bookResultSchema,
  findSlotsResultSchema,
  holdResultSchema,
} from "@magarac/shared/schemas/availability";
import { customerResponseSchema, propertyResponseSchema } from "@magarac/shared/schemas/customer";
import { jobTypeListSchema } from "@magarac/shared/schemas/jobType";
import { api } from "../../../lib/api";
import { API_BASE } from "../../../lib/auth-client";
import { messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/debug/availability")({
  component: AvailabilityTester,
});

/**
 * Not in the navigation. Exercises findSlots -> hold -> book and the live WebSocket so the
 * Phase 2 acceptance can be checked in a browser before the calendar UI exists (Phase 3).
 */
function AvailabilityTester() {
  const { active } = useTenant();
  const jobTypes = useQuery({
    queryKey: ["job-types", active?.id],
    queryFn: () => api.get("/job-types", jobTypeListSchema),
    enabled: Boolean(active),
  });
  const [jobTypeId, setJobTypeId] = useState("");
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date(Date.now() + 6 * 864e5).toISOString().slice(0, 10));
  const [address, setAddress] = useState({
    line1: "200 Oak Ln",
    city: "Austin",
    state: "TX",
    postalCode: "78745",
  });
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [events, setEvents] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const socket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!active) return;
    const base = API_BASE || window.location.origin;
    const url = `${base.replace(/^http/, "ws")}/v1/tenant/live`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      return; // blocked by the browser; the tester still works without live events
    }
    socket.current = ws;
    ws.onmessage = (m) =>
      setEvents((prev) =>
        [`${new Date().toLocaleTimeString()} ${String(m.data)}`, ...prev].slice(0, 20),
      );
    ws.onopen = () => setEvents((prev) => ["connected", ...prev]);
    ws.onclose = () => setEvents((prev) => ["closed", ...prev]);
    return () => ws.close();
  }, [active]);

  const createCustomer = useMutation({
    mutationFn: async () => {
      const c = await api.post(
        "/customers",
        { firstName: "Test", lastName: "Customer", primaryPhone: "+15125550100" },
        customerResponseSchema,
      );
      const p = await api.post(
        `/customers/${c.customer.id}/properties`,
        { address: { ...address, country: "US" } },
        propertyResponseSchema,
      );
      setCustomerId(c.customer.id);
      setPropertyId(p.property.id);
      return p.property;
    },
    onError: (err) => setError(messageFor(err)),
  });

  const slots = useMutation({
    mutationFn: () =>
      api.post(
        "/availability/slots",
        { jobTypeId, from, to, propertyId: propertyId ?? undefined },
        findSlotsResultSchema,
      ),
    onError: (err) => setError(messageFor(err)),
  });

  const book = useMutation({
    mutationFn: async (start: string) => {
      const hold = await api.post(
        "/availability/hold",
        { jobTypeId, start, propertyId: propertyId ?? undefined },
        holdResultSchema,
      );
      return api.post(
        "/availability/book",
        {
          holdId: hold.holdId,
          job: { customerId: customerId!, propertyId: propertyId ?? undefined },
        },
        bookResultSchema,
        { idempotencyKey: newId() },
      );
    },
    onSuccess: () => slots.mutate(),
    onError: (err) => setError(messageFor(err)),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="mb-4 text-2xl font-semibold">Availability tester</h1>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="1. Test customer and address">
          <Field id="l1" label="Street">
            <Input
              id="l1"
              value={address.line1}
              onChange={(e) => setAddress({ ...address, line1: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field id="c" label="City">
              <Input
                id="c"
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
              />
            </Field>
            <Field id="s" label="State">
              <Input
                id="s"
                value={address.state}
                onChange={(e) => setAddress({ ...address, state: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field id="z" label="ZIP">
              <Input
                id="z"
                value={address.postalCode}
                onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
              />
            </Field>
          </div>
          <Button onClick={() => createCustomer.mutate()} busy={createCustomer.isPending}>
            Create test customer
          </Button>
          {propertyId ? (
            <p className="mt-2 text-sm text-ok">Ready: property {propertyId.slice(0, 8)}…</p>
          ) : null}
        </Card>
        <Card title="2. Find times">
          <Field id="jt" label="Service">
            <Select id="jt" value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
              <option value="">Pick a service</option>
              {jobTypes.data?.jobTypes.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field id="from" label="From">
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field id="to" label="To">
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
          <Button onClick={() => slots.mutate()} busy={slots.isPending} disabled={!jobTypeId}>
            Find times
          </Button>
        </Card>
      </div>
      <Card title={`3. Times (${slots.data?.slots.length ?? 0})`} className="mt-4">
        {slots.data?.usedFallbackPads ? (
          <Banner tone="warn">Some drive times are estimates.</Banner>
        ) : null}
        <div className="grid max-h-96 gap-1 overflow-auto md:grid-cols-2">
          {slots.data?.slots.slice(0, 80).map((s) => (
            <div
              key={s.start}
              className="flex items-center justify-between rounded border border-line px-2 py-1 text-sm"
            >
              <span>
                {new Date(s.windowStart).toLocaleString()}{" "}
                {s.windowEnd !== s.windowStart
                  ? `to ${new Date(s.windowEnd).toLocaleTimeString()}`
                  : ""}
                <span className="text-muted">
                  {" "}
                  · {s.freeResourceIds.length} free · drive {s.travelPadMin}m ({s.travelPadSource})
                </span>
              </span>
              <Button
                variant="ghost"
                disabled={!customerId || book.isPending}
                onClick={() => book.mutate(s.start)}
              >
                Book
              </Button>
            </div>
          ))}
        </div>
      </Card>
      <Card title="Live events" className="mt-4">
        <pre className="max-h-48 overflow-auto text-xs">{events.join("\n") || "waiting…"}</pre>
      </Card>
    </div>
  );
}
